import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

const MENU_WIDTH = 176; // w-44
const VIEWPORT_PAD = 8;

/**
 * Smart ⋮ action menu: portals to document.body, stays above overflow,
 * and flips left/right/top/bottom based on available viewport space.
 */
const ActionMenu = ({ items = [], align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'bottom-end' });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;

    const rect = btn.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const menuW = menuRect.width || MENU_WIDTH;
    const menuH = menuRect.height || items.length * 40 + 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - rect.bottom - VIEWPORT_PAD;
    const spaceAbove = rect.top - VIEWPORT_PAD;
    const preferBottom = spaceBelow >= menuH || spaceBelow >= spaceAbove;
    const openUp = !preferBottom && spaceAbove > spaceBelow;

    let top = openUp
      ? Math.max(VIEWPORT_PAD, rect.top - menuH - 4)
      : Math.min(vh - menuH - VIEWPORT_PAD, rect.bottom + 4);

    // Horizontal: prefer end (right-aligned to button) when align=right
    const preferEnd = align !== 'left';
    const leftIfEnd = rect.right - menuW;
    const leftIfStart = rect.left;
    const spaceRightFromStart = vw - rect.left - VIEWPORT_PAD;
    const spaceLeftFromEnd = rect.right - VIEWPORT_PAD;

    let left;
    let hPlacement;
    if (preferEnd) {
      if (leftIfEnd >= VIEWPORT_PAD && spaceLeftFromEnd >= menuW) {
        left = leftIfEnd;
        hPlacement = 'end';
      } else if (spaceRightFromStart >= menuW) {
        left = leftIfStart;
        hPlacement = 'start';
      } else {
        left = Math.min(Math.max(VIEWPORT_PAD, leftIfEnd), vw - menuW - VIEWPORT_PAD);
        hPlacement = 'end';
      }
    } else if (spaceRightFromStart >= menuW) {
      left = leftIfStart;
      hPlacement = 'start';
    } else if (spaceLeftFromEnd >= menuW) {
      left = leftIfEnd;
      hPlacement = 'end';
    } else {
      left = Math.min(Math.max(VIEWPORT_PAD, leftIfStart), vw - menuW - VIEWPORT_PAD);
      hPlacement = 'start';
    }

    left = Math.min(Math.max(VIEWPORT_PAD, left), vw - menuW - VIEWPORT_PAD);
    top = Math.min(Math.max(VIEWPORT_PAD, top), vh - menuH - VIEWPORT_PAD);

    setCoords({
      top,
      left,
      placement: `${openUp ? 'top' : 'bottom'}-${hPlacement}`
    });
  }, [align, items.length]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    // Second pass after paint so measured menu height is accurate
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      const t = event.target;
      if (buttonRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };

    const handleKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  if (!items.length) return null;

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-placement={coords.placement}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: MENU_WIDTH,
            zIndex: 9999
          }}
          className="rounded-lg border border-slate-200 bg-white shadow-xl py-1"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onClick?.();
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition ${
                item.danger
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-slate-700 hover:bg-slate-50'
              } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {item.icon ? <item.icon className="w-4 h-4 shrink-0" /> : null}
              <span>{item.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition"
        aria-label="Actions"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {menu}
    </div>
  );
};

export default ActionMenu;
